import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onAppEvent } from '@/lib/appEvents';
import { CloseIcon } from '@/components/icons';

/**
 * Keys the lightbox keeps to itself while open. App-wide shortcuts listen on
 * document/window: Esc would also close the thread panel behind the image,
 * Ctrl/Cmd+K would open the quick switcher over it, J would jump channels.
 * Everything else (push-to-talk, copy…) goes through untouched.
 */
function isShortcutToSwallow(e: KeyboardEvent): boolean {
  const key = e.key.toLowerCase();
  if (e.ctrlKey || e.metaKey) return key === 'k' || key === '/' || key === '?' || (e.shiftKey && key === 'f');
  if (e.altKey) return false;
  return key === 'j' || key === 'о';
}

/**
 * Fullscreen image viewer. Opened via the `lightbox:open` app event (an
 * attachment image click). Click the backdrop or press Esc to close; the
 * filename + a "download" link sit in a top bar. Hosted once in HeroApp.
 *
 * A modal dialog: focus moves to the close button and stays inside (Tab
 * cycles), returns to the opener on close; the page behind doesn't scroll;
 * Esc closes only the viewer, and global shortcuts are held while it's open.
 */
export function ImageLightbox() {
  const { t } = useTranslation();
  const [img, setImg] = useState<{ url: string; name: string } | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const open = img !== null;

  useEffect(() => onAppEvent('lightbox:open', (p) => setImg(p)), []);

  // Focus in on open, back to the opener on close; no page scroll meanwhile.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (opener && opener !== document.body && opener.isConnected) opener.focus();
    };
  }, [open]);

  // Capture phase on window: runs before every document/window shortcut
  // listener, so stopping here keeps them from seeing the key at all.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setImg(null);
        return;
      }
      if (e.key === 'Tab') {
        const focusables = Array.from(
          dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        const inside = active instanceof Node && !!dialogRef.current?.contains(active);
        if (e.shiftKey && (active === first || !inside)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !inside)) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (isShortcutToSwallow(e)) e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  if (!img) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={img.name || t('cmod.lightbox.dialog')}
      className="fixed inset-0 z-[200] flex flex-col bg-black/80 backdrop-blur-sm"
      onClick={() => setImg(null)}
    >
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-white/90"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="min-w-0 flex-1 truncate">{img.name}</span>
        <a
          href={img.url}
          download={img.name}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded-lg px-2.5 py-1 text-xs text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
          {t('cmod.lightbox.download')}
        </a>
        <button
          ref={closeRef}
          type="button"
          aria-label={t('cmod.lightbox.close')}
          onClick={() => setImg(null)}
          className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
          <CloseIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-4">
        <img
          src={img.url}
          alt={img.name}
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full rounded-lg object-contain"
        />
      </div>
    </div>
  );
}
